import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash, Warning, Skull, Ghost } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';

export const EmptyWalletButton = ({ onConfirm }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  
  const confirmations = [
    { text: t('emptyConfirm1'), icon: Warning, color: 'text-yellow-500' },
    { text: t('emptyConfirm2'), icon: Ghost, color: 'text-orange-500' },
    { text: t('emptyConfirm3'), icon: Skull, color: 'text-red-500' },
  ];
  
  const handleClick = () => {
    if (!isOpen) {
      setIsOpen(true);
      setStep(0);
      return;
    }
    
    if (step < 2) {
      setStep(step + 1);
    } else {
      onConfirm();
      setIsOpen(false);
      setStep(0);
    }
  };
  
  const handleCancel = () => {
    setIsOpen(false);
    setStep(0);
  };
  
  const CurrentIcon = confirmations[step]?.icon || Warning;
  
  return (
    <div className="relative">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full right-0 mt-2 p-4 rounded-2xl bg-[var(--surface)] border border-[var(--border)] shadow-xl min-w-[300px] z-50"
          >
            <div className="flex items-center gap-3 mb-3">
              <motion.div
                key={step}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step === 0 ? 'bg-yellow-500/20' : step === 1 ? 'bg-orange-500/20' : 'bg-red-500/20'
                }`}
              >
                <CurrentIcon size={24} weight="fill" className={confirmations[step]?.color} />
              </motion.div>
              <motion.p 
                key={`text-${step}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-sm font-medium text-[var(--text)] flex-1"
              >
                {confirmations[step]?.text}
              </motion.p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="flex-1 border-[var(--border)] text-[var(--text)]"
                data-testid="empty-cancel-btn"
              >
                {t('cancel')}
              </Button>
              <Button
                size="sm"
                onClick={handleClick}
                className={`flex-1 ${
                  step === 2 ? 'bg-red-500 hover:bg-red-600' : 'bg-[var(--primary)] hover:bg-[var(--primary-hover)]'
                } text-white`}
                data-testid={`empty-confirm-step-${step}`}
              >
                {step === 2 ? t('confirm') : `${t('confirm')} (${step + 1}/3)`}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        className="text-red-500/60 hover:text-red-500 hover:bg-red-500/10 text-xs"
        data-testid="empty-wallet-btn"
      >
        <Trash size={14} className="mr-1" />
        {t('emptyAll')}
      </Button>
    </div>
  );
};
